import { Injectable } from '@angular/core';
import { filter, map, Observable, switchMap, take, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Credential, Estado } from '@models/.';
import { environment } from '@environments/environment';
import { ResponseSuccess } from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectCredential } from '@store/selectors/authentication.selectors';
import { selectStateList } from '@store/selectors';
import { transformDropdown } from '@core/helpers';
import { MenuItem } from 'primeng/api';
import { StateEnum } from '@core/enums';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  credential$: Observable<Credential | null>;
  states$: Observable<Estado[] | null>;

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.states$ = this.store.select(selectStateList);
  }

  getState(id: string): Observable<ResponseSuccess<Estado>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      switchMap((credential) => {
        if (!credential?.granted)
          return throwError(() => new Error('No credential granted'));

        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Estado>>(
          `${environment.microConfiguraciones}/api/v1/estados/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  getStateByName(name: string): Observable<Estado | undefined> {
    return this.states$.pipe(
      filter((states): states is Estado[] => !!states),
      map((states) => states.find((state) => state.descripcion === name)),
      take(1),
    );
  }

  getStateById(id?: string): Observable<string | undefined> {
    return this.states$.pipe(
      filter((states): states is Estado[] => !!states),
      map((states) => states.find((state) => state.idEstado === id)),
      map((state) => state?.descripcion),
      take(1),
    );
  }

  getStates(): Observable<ResponseSuccess<Estado>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      switchMap((credential) => {
        if (!credential?.granted)
          return throwError(() => new Error('No credential granted'));

        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Estado>>(
          `${environment.microConfiguraciones}/api/v1/estados`,
          {
            headers: { Authorization: token },
          },
        );
      }),
      take(1),
    );
  }

  getStatesCheck(): Observable<ResponseSuccess<Estado>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      switchMap((credential) => {
        if (!credential?.granted)
          return throwError(() => new Error('No credential granted'));

        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Estado>>(
          `${environment.microConfiguraciones}/api/v1/check-report/estados`,
          {
            headers: { Authorization: token },
          },
        );
      }),
      take(1),
    );
  }

  getDropdown(): Observable<MenuItem[]> {
    return this.states$.pipe(
      filter((states): states is Estado[] => !!states),
      map((states) => {
        return transformDropdown(states, 'idEstado', 'descripcion').map((state) => {
          const { name } = state;
          const [customName] = Object.entries(StateEnum)
            .find(([_, nameState]) => nameState === name) ?? []

          return {
            ...state,
            name: customName || name,
            originalName: name,
          }
        });
      }),
      take(1),
    );
  }
}
